import { ArchiveIcon, BadgeDollarSignIcon, Building2Icon, CalculatorIcon, CogIcon, CreditCardIcon, FileChartColumnIncreasingIcon, FileTextIcon, HandCoinsIcon, HomeIcon, InboxIcon, KeyIcon, LifeBuoyIcon, LightbulbIcon, type LucideIcon, ReceiptTextIcon, RocketIcon, SettingsIcon, Share2Icon, StoreIcon, UsersIcon, WalletIcon } from 'lucide-react'
import { memo, useEffect, useId, useState } from 'react'

import { Badge } from 'slab'

const ICON_MAP: Record<string, LucideIcon> = {
  // Members
  home: HomeIcon,
  'badge-dollar-sign': BadgeDollarSignIcon,
  'receipt-text': ReceiptTextIcon,
  'credit-card': CreditCardIcon,
  wallet: WalletIcon,
  archive: ArchiveIcon,
  'file-text': FileTextIcon,
  store: StoreIcon,
  // Employers
  users: UsersIcon,
  'hand-coins': HandCoinsIcon,
  'file-chart-column-increasing': FileChartColumnIncreasingIcon,
  inbox: InboxIcon,
  rocket: RocketIcon,
  calculator: CalculatorIcon,
  settings: SettingsIcon,
  // Partners
  'building-2': Building2Icon,
  'light-bulb': LightbulbIcon,
  'share-2': Share2Icon,
  key: KeyIcon,
  // Admin
  cog: CogIcon,
  // Help & support
  'life-buoy': LifeBuoyIcon,
}

type SidebarNavItemProps = {
  path?: string,
  label: string,
  icon?: string,
  showNewBadge?: boolean,
}

// Cotton candy gradient for marketplace icon
const MarketplaceGradientDefinition = ({ gradientId }: { gradientId: string }) => (
  <svg width='0' height='0' style={{ position: 'absolute', visibility: 'hidden' }}>
    <defs>
      <linearGradient id={gradientId} x1='0%' y1='0%' x2='99.24%' y2='8.72%' gradientUnits='objectBoundingBox'>
        <stop stopColor='#7DB9FE' offset='0%' />
        <stop stopColor='#F7AFE9' offset='100%' />
      </linearGradient>
    </defs>
  </svg>
)

const MARKETPLACE_CONTAINER_CLASSES = 'flex items-center justify-center rounded h-5 w-5'

const MarketplaceIcon = ({ isActive, gradientId }: { isActive: boolean, gradientId: string }) => {
  return isActive
    ? (
      <div style={{ background: 'linear-gradient(95deg, #7DB9FE 0%, #F7AFE9 100%)', stroke: 'white', fill: 'none' }} className={`${MARKETPLACE_CONTAINER_CLASSES}`}>
        <StoreIcon size={14} color='white' />
      </div>
      )
    : (
      <div className={`${MARKETPLACE_CONTAINER_CLASSES} bg-white border-emphasis`}>
        <StoreIcon size={14} style={{ stroke: `url(#${gradientId})`, fill: 'none' }} />
      </div>
      )
}

const SidebarNavItem = memo(({ path, label, icon = '', showNewBadge = false }: SidebarNavItemProps) => {
  // Normally, we could just pass isActive as a prop, but this causes sidebar flickering because
  // the react_component helper will re-renders the component on page navigation.
  const [isActive, setIsActive] = useState(path === window.location.pathname)
  // Instead of re-rendering component via isActive prop, listen for turbo:load
  // events to update isActive state. See javascript/components/index.tsx
  useEffect(() => {
    const update = () => setIsActive(window.location.pathname === path)
    document.addEventListener('turbo:load', update)
    return () => document.removeEventListener('turbo:load', update)
  }, [path])

  const IconComponent = ICON_MAP[icon] ?? null
  const isMarketplace = label === 'Marketplace'
  // Ideally we could render one <MarketplaceGradientDefinition /> for multiple sidebars, but
  // the desktop sidebar's SVG can't reference the mobile sidebar's gradient because they're in separate SVG contexts.
  // So we render one <MarketplaceGradientDefinition /> for each sidebar, and prevent ID collision.
  // Once we create the sidebar in React, we can render one <MarketplaceGradientDefinition /> for all sidebars.
  const gradientId = useId()

  // Disable prefetching for Cards and Statements to prevent phone verification on hover
  const requiresPhoneVerification = path?.includes('/cards') || path?.includes('/statements')
  const shouldDisablePrefetch = requiresPhoneVerification

  return (
    <>
      {/* Render svg cotton candy gradient for marketplace icon */}
      {isMarketplace && <MarketplaceGradientDefinition gradientId={gradientId} />}
      {/* The rounded rectangle "button" */}
      <a
        data-turbo='true' // Enable turbo drive, needed to prevent sidebar flickering
        {...(shouldDisablePrefetch && { 'data-turbo-prefetch': 'false' })} // Disable prefetching only for Cards/Statements
        href={path}
        onClick={(e) => {
          if (isActive) {
            e.preventDefault()
          }
        }}
        className={`
          group block rounded-lg p-2 cursor-pointer no-underline text-current transition-colors
          ${isActive ? 'bg-gray-200' : 'bg-transparent'}
          hover:bg-gray-200
        `}
      >
        {/* Icon */}
        <div className='flex w-full justify-start items-center gap-3'>
          {isMarketplace
            ? (<MarketplaceIcon isActive={isActive} gradientId={gradientId} />)
            : (IconComponent && (
              <IconComponent
                size={14}
                className={`
                  flex items-center justify-center rounded p-0.5 h-5 w-5
                  ${isActive ? 'text-thatch-500' : 'text-gray-500'}
                `}
              />
              )
              )}
          {/* Label */}
          <span className='body leading-5 text-gray-800'>{label}</span>
          {/* NEW badge */}
          {showNewBadge && (
            <Badge type='thatch'>
              NEW
            </Badge>
          )}
        </div>
      </a>
    </>
  )
})

export default SidebarNavItem
